import { useSnackbar } from 'notistack';
import { useState, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { ProfilePushSection } from 'src/services/applications/applications.types';
import { usePushCandidateProfileMutation } from 'src/services/applications/applications.service';
import { useLazyGetCandidateApplicationsQuery } from 'src/services/candidates/candidates.service';
import { IOrgCandidate, ICandidateApplication, OrganizationCandidateType } from 'src/services/candidates/candidates.types';

import { isProfilePushSection } from './profile-push.utils';
import ProfilePushDialog, { ProfilePushMode, ProfilePushSelection } from './profile-push-dialog';

/** More live applications than any one candidate has; the dialog lists them all. */
const MAX_APPLICATIONS = 50;

const NO_SECTIONS: ProfilePushSection[] = [];
const NO_APPLICATIONS: ICandidateApplication[] = [];

type PushState = {
  mode: ProfilePushMode;
  sections: ProfilePushSection[];
  applications: ICandidateApplication[];
};

/**
 * JT1-1808: after a save on the candidate record, offers to copy the change onto the candidate's
 * live applications. Call `prompt` once the save has succeeded, with the section keys it covered,
 * and render `dialog`. Nothing opens when the candidate has no live application that can change.
 */
export function useProfilePush(candidate?: Pick<IOrgCandidate, 'id' | 'type'>) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [fetchApplications] = useLazyGetCandidateApplicationsQuery();
  const [pushProfile, { isLoading }] = usePushCandidateProfileMutation();

  const [state, setState] = useState<PushState | null>(null);

  const close = useCallback(() => setState(null), []);

  const prompt = useCallback(
    async (sectionKeys: string[], mode: ProfilePushMode = 'section') => {
      if (!candidate) return;

      // A linked candidate owns their name, email and phone, so the API never pushes them.
      const sections = sectionKeys
        .filter(isProfilePushSection)
        .filter((section) => section !== ProfilePushSection.DETAILS || candidate.type !== OrganizationCandidateType.LINKED);
      if (sections.length === 0) return;

      try {
        const response = await fetchApplications({
          candidateId: candidate.id,
          page: 1,
          per_page: MAX_APPLICATIONS,
          live: true,
        }).unwrap();

        const applications = response?.results ?? [];
        if (!applications.some((application) => !application.profile_locked)) return;

        setState({ mode, sections, applications });
      } catch (error) {
        // The save itself went through; failing to look up the applications only means no offer.
      }
    },
    [candidate, fetchApplications]
  );

  const onConfirm = async ({ applicationIds, sections }: ProfilePushSelection) => {
    if (!candidate) return;

    try {
      const response = await pushProfile({
        organization_candidate_id: candidate.id,
        application_ids: applicationIds,
        sections,
      }).unwrap();

      if (response.skipped.length > 0) {
        enqueueSnackbar(
          t('candidates.profile_push.partial', { updated: response.updated.length, skipped: response.skipped.length }),
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(t('candidates.profile_push.success'), { variant: 'success' });
      }
      close();
    } catch (error) {
      enqueueSnackbar(t('candidates.profile_push.error'), { variant: 'error' });
    }
  };

  const dialog = (
    <ProfilePushDialog
      open={state !== null}
      mode={state?.mode ?? 'section'}
      sections={state?.sections ?? NO_SECTIONS}
      applications={state?.applications ?? NO_APPLICATIONS}
      loading={isLoading}
      onConfirm={onConfirm}
      onCancel={close}
    />
  );

  return { prompt, dialog };
}
